import React, { useCallback, useMemo, useState } from 'react'
import styled from 'styled-components/macro'
import { Paragraph } from './components'
import { EmployeeSection } from './components/EmployeeSection'
import { ErrorScreen } from './components/ErrorScreen'
import { SearchBar } from './components/SearchBar'
import { useEmployees, Employee } from './services/useEmployees'
import { useSubjects } from './services/useSubjects'

const employeeGroupsSortOrder = [
  {
    title: 'Ledelse',
    sorting: 'sortOrder'
  },
  {
    title: 'Konsulenter',
    sorting: 'alphabetically'
  },
  {
    title: 'Kommunikation',
    sorting: 'alphabetically'
  },
  {
    title: 'Administration',
    sorting: 'alphabetically'
  },
  {
    title: 'Studentermedhjælpere og praktikanter',
    sorting: 'alphabetically'
  }
]

const employeeSortValue = (a: Employee, b: Employee) => {
  const sortingForEmployeeAGroup = employeeGroupsSortOrder.find((x) => x.title === a.Group)
  const sortingForEmployeeBGroup = employeeGroupsSortOrder.find((x) => x.title === b.Group)
  if (sortingForEmployeeAGroup?.sorting === 'sortOrder' && sortingForEmployeeBGroup?.sorting === 'sortOrder') {
    return a.SortOrder - b.SortOrder
  } else {
    return a.Name.localeCompare(b.Name)
  }
}

// eslint-disable-next-line import/no-default-export
export const App = () => {
  // Filter state and filter functions
  const [subject, setSubject] = useState<string | undefined>(undefined)
  const [funktion, setFunktion] = useState<string | undefined>(undefined)
  const byFunktion = useCallback(
    (employee: Employee) => (funktion === undefined ? true : employee.Titel === funktion),
    [funktion]
  )
  const byArea = useCallback(
    (employee: Employee) =>
      subject === undefined ? true : employee.SubjectAreas.some((s) => s.toString() === subject),
    [subject]
  )
  const filterFunc = useCallback((employee: Employee) => byFunktion(employee) && byArea(employee), [byArea, byFunktion])

  // Get employees and subjects from api
  const [employees] = useEmployees()
  const [subjects] = useSubjects()

  // Exctract unique groups using the 'Groups' field
  const groups = useMemo(
    () =>
      employees.status !== 'ok'
        ? []
        : Array.from(
            new Set(
              employees.result
                .map(({ Group }) => Group)
                .filter((group) => group !== undefined && group !== '')
                .sort((a, b) => {
                  return (
                    employeeGroupsSortOrder.findIndex((x) => x.title === a) -
                    employeeGroupsSortOrder.findIndex((x) => x.title === b)
                  )
                })
            )
          ),
    [employees]
  )

  // Exctract unique funktions using the 'Titel' field
  const funktions = useMemo(
    () =>
      employees.status !== 'ok'
        ? []
        : Array.from(
            new Set(
              employees.result
                .map(({ Titel }) => Titel)
                .filter((title) => title !== undefined && title !== '')
                .sort((a, b) => a.localeCompare(b))
                .map((title) => ({ label: title, value: title }))
            )
          ),
    [employees]
  )

  // We show no loading indicator so the find-employee-app is neutrally embedable
  if (
    employees.status === 'initial' ||
    (employees.status === 'loading' && employees.result.length === 0) ||
    subjects.status === 'initial' ||
    (subjects.status === 'loading' && subjects.result.length === 0)
  ) {
    return <div />
  }

  if (employees.status === 'error') {
    return <ErrorScreen errorMessage={`${employees.error}`} />
  }

  if (subjects.status === 'error') {
    return <ErrorScreen errorMessage={`${subjects.error}`} />
  }

  // Construct a map of [employee group, employees filtered by current filter]
  const employeesByGroup = [...groups, '' /*'' represents the 'Alle' group */].reduce((acc, group) => {
    const filteredEmployees = employees.result
      .filter(({ Group }) => Group === group)
      .filter(filterFunc)
      .sort(employeeSortValue)

    if (filteredEmployees.length > 0) {
      acc[group] = filteredEmployees
    }

    return acc
  }, {} as Record<string, ReadonlyArray<Employee>>)

  // If no keys are present in the map, that means no employee matched the search
  const noEmployeeMatchesSearch = Object.keys(employeesByGroup).length === 0

  return (
    <div>
      <SearchBar
        groups={groups}
        funktions={funktions}
        subjects={subjects.result}
        subject={subject}
        funktion={funktion}
        setSubject={setSubject}
        setFunktion={setFunktion}
      />

      <Container>
        {noEmployeeMatchesSearch && <Paragraph>Ingen medarbejder matcher din søgning</Paragraph>}

        {Object.entries(employeesByGroup).map(([group, filteredEmployees]) => (
          <EmployeeSection
            key={group === '' ? 'Alle' : group}
            group={group === '' ? 'Alle' : group}
            employees={filteredEmployees}
          />
        ))}
      </Container>
    </div>
  )
}

const Container = styled.main`
  margin: 0 auto;
  width: 100%;
  max-width: 1440px;
`
